/**
 * One deal's files (Files screen 2): grouped by what they are, not where they
 * sit — with the folder view one click away for people who know the layout.
 */
import { Download, Loader2, Plus, Search, Trash2, Users } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PendingUploadRow } from "../../components/AddFiles/DealFileStatus";
import DealFilesTable from "../../components/Files/DealFilesTable";
import FileGroupNav from "../../components/Files/FileGroupNav";
import OwnerAvatar from "../../components/Files/OwnerAvatar";
import ShareFolderDialog from "../../components/Files/ShareFolderDialog";
import SharedBadge from "../../components/Files/SharedBadge";
import { useOptionalAuth } from "../../context/AuthContext";
import { isDealRoomSharingEnabled } from "../../utils/featureFlags";
import {
	canWriteFolder,
	isOwnFolder,
	shareBadgeLabel,
	shareSummary,
} from "../../utils/folderSharing";
import {
	dragHasFiles,
	useDealActivity,
	useDealRoomActions,
} from "../../hooks/useDealRoomActions";
import { useFileLibrary } from "../../hooks/useFileLibrary";
import { usePendingUploads } from "../../hooks/usePendingUploads";
import {
	type GroupBy,
	groupFiles,
	groupKey,
	ROOT_FOLDER_KEY,
} from "../../utils/fileGroups";
import BackButton from "../../components/BackButton";
import { useToast } from "../../context/ToastContext";
import { saveFileToDisk, saveFolderToDisk } from "../../services/fileApi";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";

const GROUP_BY_LABEL: Record<GroupBy, string> = {
	type: "What it is",
	folder: "Where it lives",
};

const DealFiles: React.FC = () => {
	const { dealId } = useParams<{ dealId: string }>();
	const navigate = useNavigate();
	const library = useFileLibrary();
	const actions = useDealRoomActions({
		onFolderDeleted: ({ isDeal }) => {
			if (isDeal) navigate("/files");
		},
	});
	const activityFor = useDealActivity(() => void library.refresh());
	const { showToast } = useToast();
	const downloadFile = useCallback(
		(file: { id: number }) =>
			saveFileToDisk(file.id).catch((error) =>
				showToast(friendlyErrorMessage(error, "Couldn't download this file.")),
			),
		[showToast],
	);
	const [preparingZip, setPreparingZip] = useState(false);
	const [isDropTarget, setIsDropTarget] = useState(false);
	const [groupBy, setGroupBy] = useState<GroupBy>("type");
	const [selected, setSelected] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const [sharing, setSharing] = useState(false);
	const currentUserId = useOptionalAuth()?.user?.id ?? null;

	const deal = library.findDeal(Number(dealId));
	const files = useMemo(() => deal?.files ?? [], [deal]);
	const subFolders = useMemo(() => deal?.node.sub_folders ?? [], [deal]);
	const groups = useMemo(
		() =>
			groupFiles(
				files,
				groupBy,
				subFolders.map((sub) => sub.folder.name),
			),
		[files, groupBy, subFolders],
	);
	const roots = useMemo(() => (deal ? [deal.node] : []), [deal]);
	const pending = usePendingUploads(roots).get(deal?.id ?? -1) ?? [];
	const visible = useMemo(() => {
		const q = query.trim().toLowerCase();
		return files.filter(
			(f) =>
				(selected === null || groupKey(f, groupBy) === selected) &&
				(!q || f.filename.toLowerCase().includes(q)),
		);
	}, [files, groupBy, selected, query]);

	if (library.loading) {
		return (
			<div className="flex h-full items-center justify-center gap-2 bg-white text-sm text-text-muted">
				<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
				Loading files…
			</div>
		);
	}
	if (!deal) {
		return (
			<div className="flex h-full flex-col items-start gap-3 bg-white px-8 py-10">
				<p className="text-sm text-gray-900">
					{library.error ?? "This deal isn't in your files."}
				</p>
				<Link to="/files" className="text-sm font-semibold text-[#004C4B]">
					Back to Deal Room
				</Link>
			</div>
		);
	}

	const changeGroupBy = (next: GroupBy) => {
		setGroupBy(next);
		setSelected(null);
	};

	const folder = deal.node.folder;
	const sharingOn = isDealRoomSharingEnabled();
	// A view-only teammate opens and downloads; only edit adds or deletes
	// files, and only the owner deletes the deal or shares it.
	const canWrite = canWriteFolder(folder);
	const isOwner = isOwnFolder(folder);
	const summary = sharingOn ? shareSummary(folder, currentUserId) : null;
	const addFiles = actions.addFilesTo(deal.node);
	const activity = activityFor(deal.id, deal.node.folder.project_id);
	const uploading = actions.isUploadingInto(deal.id);
	const subFolderNamed = (name: string) =>
		subFolders.find((sub) => sub.folder.name === name);
	const onDragOver = (e: React.DragEvent) => {
		if (!addFiles || !dragHasFiles(e)) return;
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
		setIsDropTarget(true);
	};

	return (
		<div
			className={`relative flex h-full flex-col bg-white ${
				isDropTarget
					? "outline-dashed outline-2 -outline-offset-4 outline-primary-500"
					: ""
			}`}
			onDragEnter={onDragOver}
			onDragOver={onDragOver}
			onDragLeave={(e) => {
				if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
				setIsDropTarget(false);
			}}
			onDrop={(e) => {
				setIsDropTarget(false);
				actions.dropFilesOn(deal.node, e);
			}}
		>
			<header className="flex h-[64px] flex-shrink-0 items-center gap-3.5 border-b border-[#eef0f2] px-8">
				{/* Back to the deal list — the same icon-only control the file page
				    uses to get back to its deal. */}
				<BackButton to="/files" label="Back to Deal Room" />
				<div className="min-w-0">
					<h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-gray-900">
						<span className="truncate">{deal.name}</span>
						{activity.busy && (
							<span
								className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-medium text-[#2A9D8F]"
								title={
									activity.readingCount > 0
										? `${activity.readingCount} file${activity.readingCount === 1 ? "" : "s"} still being processed`
										: "Processing files"
								}
							>
								<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
								{uploading ? "Uploading…" : "Reading files…"}
							</span>
						)}
						{summary && folder.shared && (
							<SharedBadge label={shareBadgeLabel(folder.shared)} />
						)}
					</h1>
					{summary && (
						<p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-text-muted">
							<OwnerAvatar name={folder.shared?.shared_by?.name} label="Shared by" />
							<span className="truncate">{summary}</span>
						</p>
					)}
				</div>
				<button
					type="button"
					onClick={() => {
						setPreparingZip(true);
						saveFolderToDisk(deal.id)
							.catch((error) =>
								showToast(friendlyErrorMessage(error, "Couldn't download this deal.")),
							)
							// The zip streams in the browser's own download bar; this only
							// covers the moment before the browser takes it over.
							.finally(() => setTimeout(() => setPreparingZip(false), 1500));
					}}
					disabled={preparingZip || files.length === 0}
					title={files.length === 0 ? "This deal has no files yet." : "Download every file in this deal as a zip"}
					className="ml-auto inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
				>
					{preparingZip ? (
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					) : (
						<Download className="h-4 w-4" aria-hidden />
					)}
					Download deal
				</button>
				{sharingOn && isOwner && (
					<button
						type="button"
						onClick={() => setSharing(true)}
						className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50"
					>
						<Users className="h-4 w-4" aria-hidden />
						Share
					</button>
				)}
				{isOwner && (
					<button
						type="button"
						onClick={() =>
							actions.requestDeleteFolder({
								node: deal.node,
								isDeal: true,
								dealId: deal.id,
							})
						}
						disabled={uploading}
						title={uploading ? "Files are still being added to this deal. You can delete once that finishes." : undefined}
						className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:border-gray-200 disabled:hover:bg-white"
					>
						<Trash2 className="h-4 w-4" aria-hidden />
						Delete deal
					</button>
				)}
				{addFiles && (
					<button
						type="button"
						onClick={() => addFiles()}
						className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#004C4B] px-3.5 text-[13px] font-semibold text-white hover:bg-[#003a3a]"
					>
						<Plus className="h-4 w-4" aria-hidden />
						Add files
					</button>
				)}
			</header>

			<div className="flex min-h-0 flex-1">
				<aside className="hidden w-[220px] flex-shrink-0 overflow-y-auto border-r border-[#f1f2f4] py-3.5 pl-6 pr-2.5 md:block">
					<FileGroupNav
						heading={GROUP_BY_LABEL[groupBy]}
						total={files.length}
						groups={groups}
						selected={selected}
						onSelect={setSelected}
						canDelete={(key) =>
							canWrite &&
							groupBy === "folder" &&
							// Deletes wait for an upload into the deal to finish.
							!uploading &&
							key !== ROOT_FOLDER_KEY &&
							subFolderNamed(key) !== undefined
						}
						onDelete={(key) => {
							const node = subFolderNamed(key);
							if (!node) return;
							if (selected === key) setSelected(null);
							actions.requestDeleteFolder({ node, isDeal: false, dealId: deal.id });
						}}
					/>
				</aside>

				<div className="flex min-w-0 flex-1 flex-col gap-2.5 overflow-y-auto px-8 py-3.5">
					<div className="flex flex-wrap items-center gap-2.5">
						<span className="text-[12.5px] text-[#5B6472]">Group by</span>
						<div
							role="group"
							aria-label="Group files by"
							className="inline-flex overflow-hidden rounded-lg border border-gray-200"
						>
							{(Object.keys(GROUP_BY_LABEL) as GroupBy[]).map((key) => (
								<button
									key={key}
									type="button"
									aria-pressed={groupBy === key}
									onClick={() => changeGroupBy(key)}
									className={`h-[30px] border-r border-gray-200 px-3 text-[12.5px] last:border-r-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004C4B] ${
										groupBy === key
											? "bg-[#E6F3F3] font-semibold text-[#004C4B]"
											: "text-gray-700 hover:bg-gray-50"
									}`}
								>
									{GROUP_BY_LABEL[key]}
								</button>
							))}
						</div>
						<label className="ml-auto flex h-8 w-[240px] items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-text-muted focus-within:border-[#004C4B]">
							<Search className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
							<input
								type="search"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								aria-label={`Search ${deal.name}'s files`}
								placeholder={`Search ${deal.name}'s files`}
								className="w-full border-0 bg-transparent p-0 text-[12.5px] text-gray-900 outline-none focus:ring-0"
							/>
						</label>
					</div>

					{isDropTarget && (
						<p className="text-xs font-medium text-primary">
							Let go to add these files to {deal.name}
						</p>
					)}

					{pending.length > 0 && (
						<div className="rounded-xl border border-gray-200 bg-white py-1">
							{pending.map((upload) => (
								<PendingUploadRow key={`pending:${upload.path}`} upload={upload} />
							))}
						</div>
					)}

					{files.length === 0 ? (
						<p className="py-6 text-sm text-text-muted">
							No files in this deal yet.
						</p>
					) : visible.length === 0 ? (
						<p className="py-6 text-sm text-text-muted">
							No files match. Clear the search or pick All files.
						</p>
					) : (
						<>
							<DealFilesTable
								files={visible}
								showAddedBy={sharingOn}
								onDelete={
									canWrite
										? (file) =>
												actions.requestDeleteFile({
													file,
													dealId: deal.id,
													dealName: deal.name,
												})
										: undefined
								}
								onDownload={downloadFile}
								deleteBlocked={uploading}
							/>
							<div className="text-xs text-text-muted">
								Showing {visible.length} of {files.length}
							</div>
						</>
					)}
				</div>
			</div>
			{actions.element}
			{sharing && (
				<ShareFolderDialog
					folderId={deal.id}
					folderName={deal.name}
					fileCount={files.length}
					subFolderCount={subFolders.length}
					current={folder.shared?.folder_id === deal.id ? folder.shared : null}
					onClose={() => setSharing(false)}
				/>
			)}
		</div>
	);
};

export default DealFiles;
